import CustomSvg from "../svgs/CustomSvg";
import { IoMdShare } from "react-icons/io";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "../../utils/hooks";
import { getRequest, postRequest } from "../apiRequests/requestApi";

// Components
import DisplayUsers from "./auxiliary/DisplayUsers";
import ShareModal from "./auxiliary/ShareModal";

// Styles
import "./styles/ActiveUsers.css";
import { IoArrowBackSharp } from "react-icons/io5";

const INITIAL_MODAL_STATE = {
  visible: false,
  onHide: null,
  size: "md",
};

export default function ActivePlayers({
  btnTxt = "share",
  btnFunc,
  gameId,
  qrValue,
  gameName,
  currentPlayerId,
  lobbyCode,
}) {
  const navigate = useNavigate();
  const { user } = useUser();
  const [shareModal, setShareModal] = useState(INITIAL_MODAL_STATE);
  const [activeUsers, setActiveUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // const { lobbyCode } = useParams();
  useEffect(() => {
    // Import socketManager dynamically to avoid circular dependencies
    let isMounted = true;
    let socketManagerModule = null;

    // Function to fetch active users through socket
    const fetchActiveUsersThroughSocket = () => {
      if (!isMounted) return;

      // Only proceed if we have socketManager
      if (!socketManagerModule) return;

      // Get socket reference
      const socket = socketManagerModule.socket;

      // If socket disconnected, try to reconnect with user data
      if (!socket.connected && user) {
        socketManagerModule.connect({
          userId: user._id,
          username: user.username,
        });
      }

      // Set up handler for active users
      const handleActiveUsers = (users) => {
        if (!isMounted) return;

        if (Array.isArray(users)) {
          // Filter out null/undefined entries and our own user
          const filteredUsers = users.filter(
            (user) => user && (user.userID || user.userId || user._id)
          );

          if (filteredUsers.length > 0) {
            setActiveUsers(filteredUsers);
            setIsLoading(false);
          } else {
            // If empty array, try API fallback
            fetchActiveUsersThroughAPI();
          }
        } else {
          // If invalid data, try API fallback
          fetchActiveUsersThroughAPI();
        }
      };

      // Remove any existing handlers to prevent duplicates
      socket.off("get_active", handleActiveUsers);

      // Add handler
      socket.on("get_active", handleActiveUsers);

      // Request active users
      // console.log("Requesting active users through socket");
      socket.emit("get_active_users");

      // Return cleanup function for this sub-effect
      return () => {
        socket.off("get_active", handleActiveUsers);
      };
    };

    // Function to fetch active users through API
    const fetchActiveUsersThroughAPI = async () => {
      if (!isMounted) return;

      try {
        setIsLoading(true);
        // console.log("Fetching active users through API");
        const response = await getRequest(`/lobby/participants/${lobbyCode}`);

        if (isMounted) {
          if (response && response.data && Array.isArray(response.data)) {
            // console.log("API returned active users:", response.data);
            setActiveUsers(response.data);
          } else {
            setActiveUsers([]);
          }
        }
      } catch (error) {
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // The main effect function
    const initializeActiveUsers = async () => {
      try {
        // First try to load socketManager
        socketManagerModule = await import("../../socket").then(
          (module) => module.default
        );

        // First attempt: try both methods
        fetchActiveUsersThroughAPI();
        const socketCleanup = fetchActiveUsersThroughSocket();

        // Set up intervals
        const apiInterval = setInterval(fetchActiveUsersThroughAPI, 30000);
        const socketInterval = setInterval(
          fetchActiveUsersThroughSocket,
          15000
        );

        // Return cleanup function
        return () => {
          clearInterval(apiInterval);
          clearInterval(socketInterval);
          if (typeof socketCleanup === "function") {
            socketCleanup();
          }
        };
      } catch (error) {
        fetchActiveUsersThroughAPI();
        const interval = setInterval(fetchActiveUsersThroughAPI, 15000);

        // Return cleanup function
        return () => clearInterval(interval);
      }
    };

    // Start the process and capture cleanup function
    const cleanup = initializeActiveUsers();

    // The main cleanup function
    return () => {
      isMounted = false;
      if (typeof cleanup === "function") {
        cleanup();
      }
    };
  }, [user, activeUsers.length]);

  const handleUserSelect = async (selectedUser) => {
    const opponentId =
      selectedUser.user_id ||
      selectedUser.userID ||
      selectedUser._id ||
      selectedUser.id;
    const opponentAvatar = selectedUser.avatar;
    const opponentName = selectedUser.username;
    if (gameId && opponentId && currentPlayerId && lobbyCode) {
      const lobbyId =
        typeof qrValue === "object" && qrValue._id ? qrValue._id : qrValue;

      // Format game name properly (capitalize first letter)
      const formattedGameName = gameName
        ? gameName.charAt(0).toUpperCase() + gameName.slice(1)
        : "Chess";

      // Make the API call to select player before navigation
      try {
        const response = await postRequest({
          url: "/gamers/select",
          data: {
            playerId: opponentId,
            lobbyId: lobbyId,
          },
        });

        // console.log("Player selection API response:", response);

        // If API call is successful, proceed with navigation
        navigate(
          `/games/selected-user/${gameId}/${currentPlayerId}/${opponentId}/${opponentName}/${lobbyId}/${lobbyCode}/${gameName}?avatar=${encodeURIComponent(
            opponentAvatar
          )}`
        );

        // console.log("URL parameters:", {
        //   gameId,
        //   playerId: currentPlayerId,
        //   opponentId,
        //   gameName: formattedGameName,
        //   lobbyId,
        //   lobbyCode,
        // });
      } catch (error) {
        // console.error("Error selecting player:", error);
        // Still navigate even if API call fails to maintain user experience
        navigate(
          `/games/selected-user/${gameId}/${currentPlayerId}/${opponentId}/${opponentName}/${lobbyId}/${lobbyCode}/${gameName}?avatar=${encodeURIComponent(
            opponentAvatar
          )}`
        );
      }
    } else if (btnFunc) {
      // Include the opponent ID in the call to the provided button function
      btnFunc({ ...selectedUser, opponentId });
    } else {
      // console.error("Missing required parameters for game navigation");
    }
  };

  const openShareModal = () => {
    setShareModal({
      ...INITIAL_MODAL_STATE,
      visible: true,
      onHide: hideShareModal,
    });
  };

  const hideShareModal = () => {
    setShareModal(INITIAL_MODAL_STATE);
  };

  return (
    <div className="w-full min-h-screen flex flex-col bg-[#130828]">
      {/* Header */}
      {/* <div className="flex items-center px-4 py-3 border-b border-white/10">
        <button onClick={() => navigate(-1)} className="text-white mr-3">
          <IoArrowBackSharp size={24} color="#fff" />
        </button>
        <span className="text-[#6FCF97] font-bold text-lg">Select Player</span>
      </div> */}

      {/* Active Users Wrapper */}
      <div className="flex flex-col w-full px-4 py-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-white text-xl font-semibold">
            Waiting <span className="text-[#FF43B9]">Lobby</span>
          </h1>
          <button
            className="text-white"
            onClick={openShareModal}
            aria-label="Share"
          >
            <IoMdShare color="#FFF" size={24} />
          </button>
        </div>

        {/* Users List */}
        <div className="w-full">
          {isLoading ? (
            <div className="text-center text-white p-4">Loading users...</div>
          ) : activeUsers.length > 0 ? (
            <DisplayUsers
              users={activeUsers
                .filter((userObj) => {
                  // Exclude logged in user and celebrities
                  const id =
                    userObj.user_id ||
                    userObj.userID ||
                    userObj._id ||
                    userObj.id;
                  const isSelf = user && (id === user._id || id === user.id);
                  const isCelebrity =
                    userObj.accountType === "celebrity" ||
                    userObj.isCelebrity === true ||
                    userObj.role === "celebrity";
                  return !isSelf && !isCelebrity;
                })
                .map((user) => {
                  const opponentId =
                    user.user_id || user.userID || user._id || user.id;
                  return { ...user, opponentId };
                })}
              btnTxt={btnTxt}
              btnFunc={
                gameId && currentPlayerId && qrValue && lobbyCode && gameName
                  ? btnFunc
                  : handleUserSelect
              }
            />
          ) : (
            <div className="text-center text-white p-4">Nobody is here</div>
          )}
        </div>

        {/* Share Modal */}
        <ShareModal
          modalProps={shareModal}
          gameName={gameName}
          lobbyCode={lobbyCode}
        />
      </div>
    </div>
  );
}
